import { ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'
import { PageShell } from '../layout/PageShell'
import { PageBrow } from '../layout/PageBrow'
import { SecondaryRail, RailItem, RailSection } from '../layout/SecondaryRail'

export interface AdminTab {
  id: string
  label: string
  icon?: ReactNode          // a lucide node, e.g. <Users size={16} />
  count?: number | string   // optional rail badge
  subtitle?: string         // shown in the brow under the label when this tab is active
  group?: string            // when set, tab renders under a RailSection header with this label
}

/**
 * Shared chrome for the admin pages (global + per-server). Kit PageShell +
 * SecondaryRail tab nav + PageBrow. The active tab is driven from the URL
 * (`?tab=<id>`, CLAUDE.md §4.8) so it survives refresh / Back-Forward; an
 * absent or unknown tab falls back to the first. `children(activeId)` renders
 * only the active tab — each tab supplies its own container (a full-height
 * TablePanel for tables, or a scrolling padded div for sections).
 */
export function AdminShell({
  title,
  icon,
  tabs,
  children,
}: {
  title: string
  icon?: ReactNode
  tabs: AdminTab[]
  children: (activeId: string) => ReactNode
}) {
  const [params, setParams] = useSearchParams()
  const requested = params.get('tab')
  const active = tabs.find((t) => t.id === requested)?.id ?? tabs[0]?.id ?? ''
  const activeTab = tabs.find((t) => t.id === active)

  const renderItem = (t: AdminTab) => (
    <RailItem
      key={t.id}
      active={t.id === active}
      icon={t.icon}
      label={t.label}
      count={t.count}
      onClick={() =>
        setParams(
          (prev) => {
            const next = new URLSearchParams(prev)
            next.set('tab', t.id)
            return next
          },
          { replace: false },
        )
      }
    />
  )

  const ungrouped = tabs.filter((t) => !t.group)
  const groupOrder: string[] = []
  for (const t of tabs) {
    if (t.group && !groupOrder.includes(t.group)) groupOrder.push(t.group)
  }

  const rail2 = (
    <SecondaryRail icon={icon} title={title}>
      {ungrouped.length > 0 && <div className="space-y-0.5">{ungrouped.map(renderItem)}</div>}
      {groupOrder.map((g) => (
        <RailSection key={g} label={g}>
          {tabs.filter((t) => t.group === g).map(renderItem)}
        </RailSection>
      ))}
    </SecondaryRail>
  )

  const brow = (
    <PageBrow
      left={
        <div className="min-w-0">
          <h1 className="truncate text-[15px] font-semibold text-foreground">{activeTab?.label ?? title}</h1>
          {activeTab?.subtitle && <p className="truncate text-xs text-muted-foreground">{activeTab.subtitle}</p>}
        </div>
      }
    />
  )

  return (
    <PageShell rail2={rail2} brow={brow} scroll={false}>
      <div className="flex min-h-0 flex-1 flex-col">{children(active)}</div>
    </PageShell>
  )
}
